<div class="recipe-image-manager">
  @if (isSearchOpen) {
      <!-- Search a stock library instead of uploading a photo -->
      <app-image-search-panel
        subject="recipe"
        [entityId]="recipeId"
        primaryActionLabel="Use as title image"
        (imported)="onImageImported()"
        (cancelled)="closeSearch()"/>
  } @else {
    <!-- Actions first: in the stacked (mobile / narrow modal) layout the preview
         is 320px tall, so anything below it starts off-screen. -->
    <div class="mb-4 flex flex-col sm:flex-row gap-2">
      <ui-button
        variant="primary"
        size="md"
        [fullWidth]="true"
        [disabled]="isUploading"
        [class.upload-drop-zone]="true"
        [class.dragging-over]="isDraggingOver"
        (clicked)="uploadImage()"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)">
        <fa-icon [icon]="isUploading ? faSpinner : faUpload" [animation]="isUploading ? 'spin' : undefined"
                 class="mr-2"></fa-icon>
        {{ isUploading ? 'Uploading…' : isDraggingOver ? 'Drop image here' : 'Add image' }}
      </ui-button>
      <ui-button
        variant="secondary"
        size="md"
        [fullWidth]="true"
        [disabled]="isUploading"
        (clicked)="openSearch()">
        <fa-icon [icon]="faSearch" class="mr-2"></fa-icon>
        Search image
      </ui-button>
      </div>

  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <!-- Left: hero preview -->
    @if (images.length > 0) {
      <div class="preview-section">
        <div class="preview-container">
          <img
            [src]="(selectedImage || images[0])?.url"
            class="preview-image"
            alt="Recipe image preview"/>

          <div class="owner-badge">
            @if (isUserOwned(selectedImage || images[0])) {
              <span class="badge badge-primary badge-sm">Uploaded</span>
            } @else if (isSearchOwned(selectedImage || images[0])) {
              <span class="badge badge-accent badge-sm">Web</span>
            } @else {
              <span class="badge badge-secondary badge-sm">Imported</span>
            }
          </div>

          <div class="primary-action">
            @if ((selectedImage || images[0])?.is_primary) {
              <div class="primary-indicator">
                <fa-icon [icon]="faStar" class="mr-2"></fa-icon>
                Title image
              </div>
            } @else {
              <ui-button
                variant="secondary"
                size="md"
                [fullWidth]="true"
                (clicked)="makePrimary()">
                <fa-icon [icon]="faStar" class="mr-2"></fa-icon>
                Use as title image
              </ui-button>
            }
          </div>
        </div>

        <!-- Credit line — mandatory for licences like Pexels' and OFF's -->
        @if (attributionFor(selectedImage || images[0]); as credit) {
          <p class="attribution-line">via {{ credit }}</p>
        }
        <div class="mt-3 flex gap-2 justify-center flex-wrap">
          <ui-button variant="danger" size="sm" (clicked)="deleteImage()">
            <fa-icon [icon]="faTrash" class="mr-1"></fa-icon>
            Delete
          </ui-button>
        </div>
      </div>
    }

    <!-- Right: upload + search + gallery -->
    <div [class]="images.length > 0 ? '' : 'md:col-span-2'">
      @if (canRefetch) {
        <div class="mb-3">
          <ui-button
            variant="secondary"
            size="md"
            [fullWidth]="true"
            [disabled]="refetchPhase !== 'idle'"
            (clicked)="startRefetch()">
            <fa-icon [icon]="faRotate" [animation]="refetchPhase === 'crawling' ? 'spin' : undefined"
                     class="mr-2"></fa-icon>
            Refetch images from source
          </ui-button>
          <p class="text-base-content/50 text-xs mt-1 text-center">
            Re-scan the original page this recipe was imported from and pick images to add.
          </p>
        </div>
      }

      @if (images.length === 0) {
        <div class="empty-state text-center p-4">
          <fa-icon [icon]="faUpload" class="empty-state-icon mb-3"></fa-icon>
          <h5 class="text-base-content/60 font-semibold">No images yet</h5>
          <p class="text-base-content/50 text-sm">Upload a photo to give this recipe a title image</p>
        </div>
      } @else {
        <div class="image-section">
          <h6 class="section-title">
            Photos
            <span class="badge badge-secondary badge-sm ml-1">{{ images.length }}</span>
          </h6>
          @if (!hasPrimary) {
            <div class="alert alert-warning py-2 px-3 mb-3" role="alert">
              <small>
                <fa-icon [icon]="faStar" class="mr-1"></fa-icon>
                Pick a title image
              </small>
            </div>
          }
          <div class="image-grid">
            @for (image of images; track image.id) {
              <div
                class="grid-item"
                [class.selected]="selectedImage?.id === image.id"
                [class.primary]="image.is_primary"
                (click)="selectImage(image)">
                <img [src]="image.url" class="grid-image" alt="Recipe image"/>
                @if (isSearchOwned(image)) {
                  <div class="grid-owner-badge">
                    <span class="badge badge-accent badge-xs">Web</span>
                  </div>
                } @else if (!isUserOwned(image)) {
                  <div class="grid-owner-badge">
                    <span class="badge badge-secondary badge-xs">Imported</span>
                  </div>
                }
                @if (image.is_primary) {
                  <div class="primary-banner">
                    <fa-icon [icon]="faStar" class="mr-1"></fa-icon>
                    Title
                  </div>
                }
                @if (selectedImage?.id === image.id) {
                  <div class="selected-overlay"></div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>
    </div>
  }

  <!-- Refetch-from-source review modal -->
  <ui-modal
    #refetchModal
    title="Images from source"
    size="lg"
    [showCloseButton]="true"
    [closeOnBackdrop]="refetchPhase !== 'applying'"
    (modalClosed)="onRefetchModalClosed()">

    @if (refetchPhase === 'crawling') {
      <div class="text-center p-6">
        <fa-icon [icon]="faSpinner" animation="spin" class="text-2xl mb-3 text-primary"></fa-icon>
        <p class="text-base-content/70">Re-scanning the source page for images…</p>
      </div>
    } @else {
      @if (refetchError) {
        <div class="alert alert-warning py-2 px-3 mb-3" role="alert">
          <small>{{ refetchError }}</small>
        </div>
      }

      @if (refetchImages.length > 0) {
        <p class="text-base-content/60 text-sm mb-3">
          Choose which images to add to this recipe, and pick the title (hero) image.
        </p>
        <div class="refetch-grid">
          @for (image of refetchImages; track image.name) {
            <div class="refetch-item" [class.kept]="image.keep" [class.title]="image.keep && image.isTitle">
              <div class="refetch-thumb" (click)="toggleRefetchKeep(image.name)">
                @if (image.objectUrl) {
                  <img [src]="image.objectUrl" class="refetch-image" alt="Proposed recipe image"/>
                } @else {
                  <div class="refetch-loading">
                    <fa-icon [icon]="faSpinner" animation="spin"></fa-icon>
                  </div>
                }
                @if (image.keep) {
                  <div class="refetch-keep-badge">
                    <fa-icon [icon]="faCheck"></fa-icon>
                  </div>
                }
                @if (image.keep && image.isTitle) {
                  <div class="refetch-title-banner">
                    <fa-icon [icon]="faStar" class="mr-1"></fa-icon>
                    Title
                  </div>
                }
              </div>
              <div class="refetch-actions">
                <label class="refetch-keep-toggle">
                  <input type="checkbox" class="checkbox checkbox-sm" [checked]="image.keep"
                         (change)="toggleRefetchKeep(image.name)"/>
                  <span class="text-xs">Keep</span>
                </label>
                <button type="button" class="refetch-title-btn"
                        [class.active]="image.keep && image.isTitle"
                        [disabled]="!image.keep"
                        (click)="setRefetchTitle(image.name)">
                  <fa-icon [icon]="faStar" class="mr-1"></fa-icon>
                  Title
                </button>
              </div>
            </div>
          }
        </div>
      } @else if (!refetchError) {
        <div class="text-center p-6 text-base-content/60">
          No images were found on the source page.
        </div>
      }
    }

    <div modal-footer class="flex gap-2 justify-end w-full">
      <ui-button variant="ghost" size="md" [disabled]="refetchPhase === 'applying'" (clicked)="closeRefetch()">
        Cancel
      </ui-button>
      <ui-button
        variant="primary"
        size="md"
        [disabled]="refetchPhase !== 'review' || refetchKeptCount === 0"
        (clicked)="applyRefetch()">
        <fa-icon [icon]="refetchPhase === 'applying' ? faSpinner : faCheck"
                 [animation]="refetchPhase === 'applying' ? 'spin' : undefined" class="mr-2"></fa-icon>
        {{ refetchPhase === 'applying' ? 'Adding…' : 'Add ' + refetchKeptCount + ' image' + (refetchKeptCount === 1 ? '' : 's') }}
      </ui-button>
    </div>
  </ui-modal>
</div>
